import React, { createContext, useContext, useState, useEffect } from 'react';
import { scheduleStreakReminder } from '../utils/notifications';

const GameContext = createContext();

const STATS_KEY   = 'bibleapp_stats';
const PROFILE_KEY = 'bibleapp_profile';
const ESCAPE_KEY  = 'bibleapp_escape_badges';
const BEST_KEY    = 'bibleapp_best_scores';
const AUTH_KEY    = 'bibleapp_auth';

const AVATARS = ['🦁','✝️','📖','🌿','🏛️','⚡','👑','🌟','🕊️','🔥','🌈','⭐'];

const DEFAULT_STATS   = { xp: 0, totalQuizzes: 0, totalCorrect: 0, gamesPlayed: {} };
const DEFAULT_PROFILE = { name: '', avatar: '📖', setupDone: false };
const DEFAULT_STREAK  = { current: 0, best: 0, lastPlayedDate: '' };

export { AVATARS };

// ── Courbe XP triangulaire ────────────────────────────────────────────────────
// Niveau n nécessite n*(n-1)/2*100 XP au total.
export const xpForLevel = (n) => n <= 1 ? 0 : (n - 1) * n / 2 * 100;

const computeLevel = (xp) => {
  let n = 1;
  while (xpForLevel(n + 1) <= xp) n++;
  return n;
};

// ── Coûts des indices par jeu ─────────────────────────────────────────────────
// Un indice RÉDUIT le gain de la partie en cours ; il ne retire jamais d'XP déjà
// acquise. Ces valeurs doivent rester identiques à HINT_COSTS_BY_GAME du serveur.
export const HINT_COSTS = {
  speedrun: 5,
  bingo:    5,
  escape:   10,
  memory:   0,
  // fix V1 : coût unique quelle que soit la difficulté, aligné sur le serveur.
  battle:   8,
  secretkey: 0,
};

// ── Helpers API ───────────────────────────────────────────────────────────────
const API_BASE = (process.env.REACT_APP_API_URL || 'https://bibleduo-game-production.up.railway.app').replace(/\/$/, '');

function getToken() {
  try {
    const raw = localStorage.getItem(AUTH_KEY);
    return raw ? JSON.parse(raw).token : null;
  } catch { return null; }
}

// Fetch authentifié : dispatch 'auth:unauthorized' sur 401 pour déclencher le logout auto.
async function authFetch(url, options) {
  const res = await fetch(url, options);
  if (res.status === 401) window.dispatchEvent(new Event('auth:unauthorized'));
  return res;
}

// Sync profil uniquement (plus jamais d'XP via PATCH)
async function syncProfile(profile) {
  const token = getToken();
  if (!token) return;
  try {
    await authFetch(`${API_BASE}/api/users/me/stats`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ profile }),
    });
  } catch {}
}

export function GameProvider({ children, token }) {
  const [stats, setStats] = useState(() => {
    try { const s = localStorage.getItem(STATS_KEY); return s ? { ...DEFAULT_STATS, ...JSON.parse(s) } : DEFAULT_STATS; }
    catch { return DEFAULT_STATS; }
  });
  const [profile, setProfile] = useState(() => {
    try { const p = localStorage.getItem(PROFILE_KEY); return p ? { ...DEFAULT_PROFILE, ...JSON.parse(p) } : DEFAULT_PROFILE; }
    catch { return DEFAULT_PROFILE; }
  });
  const [escapeBadges, setEscapeBadges] = useState(() => {
    try { const e = localStorage.getItem(ESCAPE_KEY); return e ? JSON.parse(e) : []; }
    catch { return []; }
  });
  const [bestScores, setBestScores] = useState(() => {
    try { const b = localStorage.getItem(BEST_KEY); return b ? JSON.parse(b) : {}; }
    catch { return {}; }
  });
  const [streak, setStreak] = useState(DEFAULT_STREAK);

  // ── Hydratation / reset piloté par le token d'authentification ───────────
  useEffect(() => {
    // fix : drapeau d'annulation — une exécution devenue obsolète après un
    // changement de token ne doit plus pouvoir modifier les states.
    let cancelled = false;

    // fix : logout ou suppression → remise à zéro des données joueur en mémoire
    // et du cache local, pour qu'un autre compte ne voie jamais celles du précédent.
    if (!token) {
      setStats({ ...DEFAULT_STATS });
      setProfile({ ...DEFAULT_PROFILE });
      setEscapeBadges([]);
      setBestScores({});
      setStreak({ ...DEFAULT_STREAK });
      [STATS_KEY, PROFILE_KEY, ESCAPE_KEY, BEST_KEY].forEach((k) => localStorage.removeItem(k));
      return () => { cancelled = true; };
    }

    (async () => {
      // 1. Charger les données utilisateur depuis l'API (source de vérité)
      try {
        const r = await authFetch(`${API_BASE}/api/users/me`, { headers: { Authorization: `Bearer ${token}` } });
        if (cancelled) return;
        if (r.ok) {
          const data = await r.json();
          if (cancelled) return;
          if (data.stats)   setStats({ ...DEFAULT_STATS, ...data.stats });
          if (data.profile) setProfile({ ...DEFAULT_PROFILE, ...data.profile });
          if (data.streak)  setStreak({ ...DEFAULT_STREAK, ...data.streak });
        }
      } catch {}

      // 2. Check-in quotidien — bonus XP géré par le serveur via daily-checkin
      try {
        const r = await authFetch(`${API_BASE}/api/users/me/daily-checkin`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        if (r.ok) {
          const data = await r.json();
          if (cancelled) return;
          setStreak(data.streak);
          scheduleStreakReminder(data.streak.current).catch(() => {});
          if (!data.alreadyDone && data.stats) {
            setStats({ ...DEFAULT_STATS, ...data.stats });
          }
        }
      } catch {}
    })();

    return () => { cancelled = true; };
  }, [token]); // eslint-disable-line

  useEffect(() => { if (token) localStorage.setItem(STATS_KEY,   JSON.stringify(stats));        }, [stats, token]);
  useEffect(() => { if (token) localStorage.setItem(PROFILE_KEY, JSON.stringify(profile));      }, [profile, token]);
  useEffect(() => { if (token) localStorage.setItem(ESCAPE_KEY,  JSON.stringify(escapeBadges)); }, [escapeBadges, token]);
  useEffect(() => { if (token) localStorage.setItem(BEST_KEY,    JSON.stringify(bestScores));   }, [bestScores, token]);

  // Envoie le résultat d'une partie au serveur (source de vérité XP).
  // Le serveur calcule l'XP via computeSessionXp et répond avec les stats réelles.
  // fix : retourne { stats, xpGained } quand le serveur a bien enregistré la
  // session, null sinon (pas de token, réponse non OK, erreur réseau). L'appelant
  // n'annonce un gain XP que sur confirmation. Aucune erreur remontée au jeu.
  const reportSession = async ({ game, correct, total, hintsUsed = 0 }) => {
    const token = getToken();
    if (!token) return null;
    try {
      const r = await authFetch(`${API_BASE}/api/sessions/result`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ game, correct, total, hintsUsed }),
      });
      if (!r.ok) return null;
      const { stats: serverStats, xpGained } = await r.json();
      if (serverStats) setStats({ ...DEFAULT_STATS, ...serverStats });
      return { stats: serverStats, xpGained };
    } catch {
      return null;
    }
  };

  const saveProfile = (name, avatar) => {
    const next = { name: name.trim() || 'Anonyme', avatar, setupDone: true };
    setProfile(next);
    syncProfile(next);
  };

  const addEscapeBadge = (badge) => {
    setEscapeBadges(prev =>
      prev.find(b => b.id === badge.id) ? prev : [...prev, badge]
    );
  };

  const updateBestScore = (gameId, score) => {
    setBestScores(prev => ({
      ...prev,
      [gameId]: Math.max(prev[gameId] || 0, score),
    }));
  };

  // ── Calculs niveau / XP ───────────────────────────────────────────────────
  const level      = computeLevel(stats.xp);
  const currentXP  = xpForLevel(level);
  const nextXP     = xpForLevel(level + 1);
  const xpCurrent  = stats.xp - currentXP;
  const xpNeeded   = nextXP - currentXP;
  const xpProgress = Math.min(100, Math.floor(xpCurrent / xpNeeded * 100));

  return (
    <GameContext.Provider value={{
      stats, level, xpProgress, xpCurrent, xpNeeded,
      profile, saveProfile,
      reportSession,
      escapeBadges, addEscapeBadge,
      bestScores, updateBestScore,
      streak,
      AVATARS,
    }}>
      {children}
    </GameContext.Provider>
  );
}

export function useGame() { return useContext(GameContext); }
